import { INTERNSHIPS_DATA } from '@lib/constants';
import Icon from '@components/common/Icon';

const TimelineSection = () => {
  // Use centralized data and transform it for timeline display
  const timelineEvents = INTERNSHIPS_DATA.filter(item => item.highlights).map((item, idx) => ({
    id: idx + 1,
    date: item.date,
    company: item.company,
    location: item.type === 'ai-ml' ? `${item.location} (Remote)` : item.location,
    role: item.role,
    duration: item.duration,
    icon: item.icon,
    highlights: item.highlights,
    type: item.type,
  }));

  const getTypeColor = (type) => {
    switch (type) {
      case 'retail-tech':
        return 'var(--color-accent-5)';
      case 'robotics':
        return 'var(--color-accent-7)';
      case 'ai-ml':
        return 'var(--color-accent-6)';
      default:
        return 'var(--color-accent-6)';
    }
  };

  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-5xl">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2
            className="text-4xl sm:text-5xl font-heading font-bold"
            style={{ color: 'var(--color-heading)' }}
          >
            Career Timeline
          </h2>
          <p
            className="text-lg max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Professional growth from retail technology to artificial intelligence
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div
            className="absolute left-8 top-0 bottom-0 w-0.5 hidden md:block"
            style={{ backgroundColor: 'var(--color-border-medium)' }}
          ></div>

          {/* Timeline Events */}
          <div className="space-y-12">
            {timelineEvents.map((event, idx) => (
              <div key={event.id} className="relative">
                {/* Timeline Dot */}
                <div className="absolute left-8 -translate-x-1/2 hidden md:flex items-center justify-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center z-10 shadow-card"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: getTypeColor(event.type),
                      borderWidth: '3px',
                      borderStyle: 'solid'
                    }}
                  >
                    <Icon name={event.icon} size="2xl" className="text-4xl" style={{ color: getTypeColor(event.type) }} />
                  </div>
                </div>

                {/* Timeline Content */}
                <div className="md:ml-32">
                  <div
                    className="rounded-xl p-6 transition-all duration-300 hover:shadow-card-hover"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: 'var(--color-card-border)',
                      borderWidth: '1px',
                      borderStyle: 'solid'
                    }}
                  >
                    {/* Date Badge */}
                    <div
                      className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold mb-4"
                      style={{
                        backgroundColor: 'var(--color-theme-2)',
                        color: getTypeColor(event.type)
                      }}
                    >
                      {event.date}
                    </div>

                    {/* Company and Role */}
                    <div className="mb-4">
                      <h3
                        className="text-2xl font-bold mb-1"
                        style={{ color: 'var(--color-heading)' }}
                      >
                        {event.company}
                      </h3>
                      <div
                        className="text-sm mb-1"
                        style={{ color: 'var(--color-muted)' }}
                      >
                        {event.location}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        <span
                          className="font-semibold"
                          style={{ color: 'var(--color-subheading)' }}
                        >
                          {event.role}
                        </span>
                        <span
                          className="text-sm"
                          style={{ color: 'var(--color-muted)' }}
                        >
                          • {event.duration}
                        </span>
                      </div>
                    </div>

                    {/* Highlights */}
                    <div>
                      <h4
                        className="text-sm font-semibold mb-3"
                        style={{ color: 'var(--color-subheading)' }}
                      >
                        Key Highlights
                      </h4>
                      <ul className="space-y-2">
                        {event.highlights.map((highlight, highlightIdx) => (
                          <li
                            key={highlightIdx}
                            className="text-sm flex items-start"
                            style={{ color: 'var(--color-subtle)' }}
                          >
                            <span
                              className="mr-2 mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: getTypeColor(event.type) }}
                            ></span>
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Mobile Timeline Dot (shown on mobile) */}
                <div className="md:hidden flex items-center justify-center mb-4">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: getTypeColor(event.type),
                      borderWidth: '2px',
                      borderStyle: 'solid'
                    }}
                  >
                    <Icon name={event.icon} size="2xl" className="text-4xl" style={{ color: getTypeColor(event.type) }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Stats - Horizontal scroll on mobile, grid on desktop */}
        <div className="mt-16">
          {/* Mobile: Horizontal scroll */}
          <div className="sm:hidden flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
            <div
              className="text-center p-6 rounded-xl min-w-[160px] flex-shrink-0 snap-center"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <div
                className="text-3xl font-bold mb-2"
                style={{ color: 'var(--color-accent-6)' }}
              >
                3
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: 'var(--color-subtle)' }}
              >
                Internships
              </div>
            </div>
            <div
              className="text-center p-6 rounded-xl min-w-[160px] flex-shrink-0 snap-center"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <div
                className="text-3xl font-bold mb-2"
                style={{ color: 'var(--color-accent-6)' }}
              >
                2
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: 'var(--color-subtle)' }}
              >
                Countries
              </div>
            </div>
            <div
              className="text-center p-6 rounded-xl min-w-[160px] flex-shrink-0 snap-center"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <div
                className="text-3xl font-bold mb-2"
                style={{ color: 'var(--color-accent-6)' }}
              >
                10+
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: 'var(--color-subtle)' }}
              >
                Skills Gained
              </div>
            </div>
          </div>

          {/* Desktop: Grid */}
          <div className="hidden sm:grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div
              className="text-center p-6 rounded-xl"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <div
                className="text-3xl font-bold mb-2"
                style={{ color: 'var(--color-accent-6)' }}
              >
                3
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: 'var(--color-subtle)' }}
              >
                Internships
              </div>
            </div>
            <div
              className="text-center p-6 rounded-xl"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <div
                className="text-3xl font-bold mb-2"
                style={{ color: 'var(--color-accent-6)' }}
              >
                2
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: 'var(--color-subtle)' }}
              >
                Countries
              </div>
            </div>
            <div
              className="text-center p-6 rounded-xl"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <div
                className="text-3xl font-bold mb-2"
                style={{ color: 'var(--color-accent-6)' }}
              >
                10+
              </div>
              <div
                className="text-sm font-medium"
                style={{ color: 'var(--color-subtle)' }}
              >
                Skills Gained
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;
